import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const scrollToSection = (id) => {
    if (!isHome) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  const navLinks = [
    { label: "About", href: isHome ? "#about" : "/#about", section: "about" },
    { label: "Services", href: isHome ? "#services" : "/#services", section: "services" },
    { label: "Pet Pantry", href: "/pet-pantry" },
    { label: "Get Involved", href: isHome ? "#get-involved" : "/#get-involved", section: "get-involved" },
    { label: "Contact", href: isHome ? "#contact" : "/#contact", section: "contact" },
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-[#E8E3DD]/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Heart className="w-6 h-6 text-[#C0603A] fill-[#C0603A] group-hover:scale-110 transition-transform" />
          <span className="font-heading text-xl md:text-2xl font-semibold text-[#2C2C2A] tracking-tight">
            333Oreo
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.section && isHome ? (
              <button
                key={link.label}
                onClick={() => scrollToSection(link.section)}
                className="font-body text-sm font-medium text-[#6C6B66] hover:text-[#C0603A] transition-colors"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href.startsWith("#") ? "/" : link.href}
                className="font-body text-sm font-medium text-[#6C6B66] hover:text-[#C0603A] transition-colors"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/donate"
            data-testid="nav-donate-button"
            className="bg-[#C0603A] text-white hover:bg-[#A64D29] transition-colors rounded-full px-6 py-2.5 font-body text-sm font-semibold inline-flex items-center gap-2"
          >
            <Heart className="w-4 h-4" />
            Donate
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-[#2C2C2A]"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#E8E3DD]"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href.startsWith("#") ? "/" : link.href}
                  className="font-body text-base text-[#3A3A38] py-2 hover:text-[#C0603A] transition-colors"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => {
                    if (link.section && isHome) scrollToSection(link.section);
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/donate"
                data-testid="mobile-donate-button"
                className="bg-[#C0603A] text-white rounded-full px-6 py-3 font-body text-sm font-semibold text-center mt-2"
              >
                Donate Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
